import React from 'react';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Link } from '@latitude/link';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const LINK_ITEM_CONTENT_TYPE = 'menuItems';

const getEntryContentType = entry =>
  entry?.contentType?.sys?.id || entry?.sys?.contentType?.sys?.id;

const getTrackEventData = (entry, fallbackLocation) => ({
  category: 'text-link',
  action: entry?.link?.startsWith('http') ? 'external-link' : 'internal-link',
  location: entry?.location || fallbackLocation,
  label: entry?.trackEventData || entry?.label || entry?.title
});

const renderInlineEntry = location => node => {
  const entry = node?.data?.target;

  if (getEntryContentType(entry) !== LINK_ITEM_CONTENT_TYPE || !entry?.link) {
    return null;
  }

  const label = entry.label || entry.title;

  return (
    <Link
      noStyle
      style={{ textDecoration: 'underline' }}
      href={entry.link}
      trackEventData={getTrackEventData(entry, location)}
    >
      {label}
    </Link>
  );
};

const richTextOptions = location =>
  getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
      [INLINES.EMBEDDED_ENTRY]: renderInlineEntry(location)
    }
  });

const detailsRichTextOptions = location =>
  getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Typography.Body color={REBRAND_COLOR.BLACK_80}>
          {children}
        </Typography.Body>
      ),
      [INLINES.EMBEDDED_ENTRY]: renderInlineEntry(location)
    }
  });

const renderRichText = (richText, location) =>
  richText?.nodeType === 'document'
    ? documentToReactComponents(richText, richTextOptions(location))
    : undefined;

const renderDetailsRichText = (richText, location) =>
  richText?.nodeType === 'document'
    ? documentToReactComponents(richText, detailsRichTextOptions(location))
    : undefined;

const mapButton = button =>
  button?.link
    ? {
        href: button.link,
        btnText: button.label,
        trackid: button.trackEventData,
        trackEventData: getTrackEventData(button, button.location)
      }
    : undefined;

const getAssetUrl = asset => asset?.file?.url || asset?.fields?.file?.url;

const mapCard = card => {
  const location = `feature-card-list-${card.title}`;

  return {
    code: card.code,
    id: card.cardId || card.id,
    title: card.title,
    description: renderRichText(card.description, location) || card.description,
    offerBadge: card.offerBadge,
    mainOffer: renderRichText(card.mainOffer, location) || card.mainOffer,
    image: getAssetUrl(card.image) || card.imageUrl,
    detailsContent: renderDetailsRichText(card.details, location),
    details: Array.isArray(card.details)
      ? card.details
          .map(detail => renderDetailsRichText(detail?.content, location))
          .filter(Boolean)
      : [],
    secBtn: mapButton(card.primaryButton),
    buttonProps: mapButton(card.secondaryButton)
  };
};

export const getFeatureCardListRebrandDataFromContentful = data => {
  if (!data?.cards?.length) return null;

  return {
    title: data.title,
    tcText: data.tcText,
    cardList: data.cards.map(mapCard)
  };
};
